// The fetch state the kit reads (a useQuery result fits as is). Under a loading
// component `useLoading()` is true and value components draw skeletons.
import {
  computed,
  inject,
  provide,
  toValue,
  type ComputedRef,
  type InjectionKey,
  type MaybeRefOrGetter,
  type Ref,
} from "vue";

import globals from "@/globals";
import { isApiError, isHandled } from "@/services/apiError";

export interface LoadState<T = unknown> {
  data: Readonly<Ref<T | undefined>>;
  isPending: Readonly<Ref<boolean>>;
  isFetching: Readonly<Ref<boolean>>;
  isError: Readonly<Ref<boolean>>;
  error: Readonly<Ref<unknown>>;
  isPlaceholderData?: Readonly<Ref<boolean>>;
  refetch: () => Promise<unknown>;
}

type DataOf<S> = S extends LoadState<infer T> ? T : never;

export function combineLoads<const S extends readonly LoadState[]>(
  ...states: S
): LoadState<{ [K in keyof S]: DataOf<S[K]> }> {
  type Tuple = { [K in keyof S]: DataOf<S[K]> };
  return {
    data: computed(() =>
      states.every((s) => s.data.value !== undefined)
        ? (states.map((s) => s.data.value) as unknown as Tuple)
        : undefined,
    ),
    isPending: computed(() => states.some((s) => s.isPending.value)),
    isFetching: computed(() => states.some((s) => s.isFetching.value)),
    isError: computed(() => states.some((s) => s.isError.value)),
    error: computed(() => states.find((s) => s.isError.value)?.error.value ?? null),
    isPlaceholderData: computed(() => states.some((s) => s.isPlaceholderData?.value === true)),
    refetch: () =>
      Promise.all(states.filter((s) => s.isError.value || s.data.value === undefined).map((s) => s.refetch())),
  };
}

export function staticLoad<T>(data: MaybeRefOrGetter<T>): LoadState<T> {
  const no = computed(() => false);
  return {
    data: computed(() => toValue(data)),
    isPending: no,
    isFetching: no,
    isError: no,
    error: computed(() => null),
    isPlaceholderData: no,
    refetch: async () => undefined,
  };
}

export type LoadPhase = "loading" | "error" | "empty" | "ready";

// No data to show yet: still waiting, a failure being retried, or a failure the
// response policy acted on, which is a redirect under way and never shown.
export function awaitingData(state: LoadState): boolean {
  if (state.data.value !== undefined) return false;
  if (!state.isError.value || state.isFetching.value) return true;
  return isHandled(state.error.value);
}

// A failure with data still on screen is "ready"; the error shows above it.
export function loadPhaseOf<T>(state: LoadState<T>, isEmpty: (data: T) => boolean = defaultIsEmpty): LoadPhase {
  const data = state.data.value;
  if (data !== undefined) return isEmpty(data) ? "empty" : "ready";
  return awaitingData(state) ? "loading" : "error";
}

// The failure as a sentence, or "" when there is none to show.
export function loadErrorText(state: LoadState, t: Parameters<typeof globals.renderError>[0]): string {
  const error = state.error.value;
  if (!state.isError.value || isHandled(error)) return "";
  return globals.renderError(t, error);
}

// What kind of failure it was, which LoadError draws as its icon.
export type LoadErrorKind = "unreachable" | "server" | "denied" | "other";

export function loadErrorKind(error: unknown): LoadErrorKind {
  if (!isApiError(error)) return "other";
  if (error.kind === "network" || error.kind === "timeout") return "unreachable";
  if (error.status === 403) return "denied";
  if (error.status >= 500) return "server";
  return "other";
}

// For `:is-empty` where every answer has something to draw.
export const neverEmpty = () => false;

export function defaultIsEmpty(data: unknown): boolean {
  if (data === null) return true;
  if (Array.isArray(data)) return data.length === 0;
  return false;
}

const loadingKey: InjectionKey<ComputedRef<boolean>> = Symbol("loading");

export function provideLoading(pending: MaybeRefOrGetter<boolean>): void {
  const outer = inject(loadingKey, null);
  provide(
    loadingKey,
    computed(() => outer?.value === true || toValue(pending)),
  );
}

export function useLoading(): ComputedRef<boolean> {
  const outer = inject(loadingKey, null);
  return computed(() => outer?.value === true);
}
